import { useState, useEffect } from "react";
import apiRequest, { API_URLS } from "../services/api";
import Alert from "../components/Alert";

// ─── shared icon primitive ────────────────────────────────────────────────────
const Icon = ({ d, className = "w-5 h-5" }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d={d} />
  </svg>
);

const ICONS = {
  users:   "M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z",
  user:    "M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z",
  plus:    "M12 4v16m8-8H4",
  edit:    "M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z",
  trash:   "M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16",
  warning: "M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z",
  x:       "M6 18L18 6M6 6l12 12",
  search:  "M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z",
  globe:   "M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z",
  phone:   "M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z",
  mail:    "M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z",
  calendar:"M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z",
  check:   "M5 13l4 4L19 7",
};

const PALETTE = [
  "bg-blue-600","bg-emerald-600","bg-violet-600","bg-indigo-600",
  "bg-amber-500","bg-teal-600","bg-rose-600","bg-cyan-600",
  "bg-orange-500","bg-purple-600",
];

const EMPTY_FORM = { name: "", email: "", work_number: "", region_id: "", role: "enumerator" };
const inputCls = "w-full px-3.5 py-2.5 text-sm border border-slate-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition bg-slate-50 focus:bg-white";

// ─── Field label wrapper ──────────────────────────────────────────────────────
const Field = ({ label, required, children }) => (
  <div>
    <label className="block text-xs font-semibold text-slate-600 uppercase tracking-wider mb-1.5">
      {label} {required && <span className="text-red-500">*</span>}
    </label>
    {children}
  </div>
);

// ─── Modal shell ─────────────────────────────────────────────────────────────
const Modal = ({ icon, iconBg = "bg-blue-600", title, subtitle, onClose, children, footer }) => (
  <div className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
    <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md max-h-[92vh] flex flex-col">
      <div className="flex items-center justify-between px-6 py-5 border-b border-slate-100 shrink-0">
        <div className="flex items-center gap-3">
          <div className={`w-9 h-9 ${iconBg} rounded-lg flex items-center justify-center shrink-0`}>
            <Icon d={icon} className="w-4 h-4 text-white" />
          </div>
          <div>
            <h2 className="text-base font-bold text-slate-900">{title}</h2>
            {subtitle && <p className="text-xs text-slate-400">{subtitle}</p>}
          </div>
        </div>
        <button onClick={onClose} className="w-8 h-8 rounded-lg flex items-center justify-center text-slate-400 hover:bg-slate-100 hover:text-slate-600 transition-colors">
          <Icon d={ICONS.x} className="w-4 h-4" />
        </button>
      </div>
      <div className="flex-1 overflow-y-auto px-6 py-5 space-y-4">{children}</div>
      {footer && <div className="px-6 pb-6 pt-2 shrink-0">{footer}</div>}
    </div>
  </div>
);

const EnumeratorFormBody = ({ formData, onChange, regions }) => (
  <>
    <Field label="Full Name" required>
      <input dir="ltr" type="text" value={formData.name}
        onChange={e => onChange("name", e.target.value)}
        className={inputCls} placeholder="e.g. Jane Mwangi" autoFocus />
    </Field>
    <Field label="Email Address" required>
      <input dir="ltr" type="email" value={formData.email}
        onChange={e => onChange("email", e.target.value)}
        className={inputCls} placeholder="jane@example.com" />
    </Field>
    <Field label="Work Number">
      <input dir="ltr" type="text" value={formData.work_number}
        onChange={e => onChange("work_number", e.target.value)}
        className={inputCls} placeholder="SWK001" />
    </Field>
    <Field label="Region" required>
      <div className="relative">
        <select value={formData.region_id} onChange={e => onChange("region_id", e.target.value)}
          className={inputCls + " appearance-none pr-9"}>
          <option value="">Select a region</option>
          {regions.map(r => <option key={r.id} value={r.id}>{r.name}</option>)}
        </select>
        <div className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-slate-400">
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </div>
      </div>
    </Field>
  </>
);

// ─── Buttons — also outside for stable identity ───────────────────────────────
const SubmitBtn = ({ label, loadingLabel, onClick, submitting }) => (
  <button onClick={onClick} disabled={submitting}
    className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-300 disabled:cursor-not-allowed text-white text-sm font-semibold rounded-xl transition-colors">
    {submitting
      ? <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
      : <Icon d={ICONS.check} className="w-4 h-4" />}
    {submitting ? loadingLabel : label}
  </button>
);

const CancelBtn = ({ onClick }) => (
  <button onClick={onClick}
    className="flex-1 px-4 py-2.5 bg-slate-100 hover:bg-slate-200 text-slate-700 text-sm font-semibold rounded-xl transition-colors">
    Cancel
  </button>
);

// ─────────────────────────────────────────────────────────────────────────────
export default function Enumerators() {
  const [enumerators, setEnumerators]   = useState([]);
  const [regions, setRegions]           = useState([]);
  const [loading, setLoading]           = useState(true);
  const [error, setError]               = useState("");
  const [submitting, setSubmitting]     = useState(false);
  const [search, setSearch]             = useState("");
  const [filterRegion, setFilterRegion] = useState("");

  const [showCreate, setShowCreate]     = useState(false);
  const [showEdit, setShowEdit]         = useState(false);
  const [showDelete, setShowDelete]     = useState(false);
  const [toEdit, setToEdit]             = useState(null);
  const [toDelete, setToDelete]         = useState(null);
  const [formData, setFormData]         = useState(EMPTY_FORM);

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try {
      const [er, rr] = await Promise.all([
        apiRequest(API_URLS.admin.listEnumerators),
        apiRequest(API_URLS.admin.getRegions),
      ]);
      setEnumerators(er.data || []);
      setRegions(rr.data || []);
    } catch (err) { setError(err.message || "Failed to fetch data"); }
    finally { setLoading(false); }
  };

  const handleFieldChange = (key, value) => setFormData(f => ({ ...f, [key]: value }));

  const handleCreate = async () => {
    if (!formData.name || !formData.email || !formData.region_id) { setError("Name, email and region are required"); return; }
    setSubmitting(true);
    try {
      await apiRequest(API_URLS.admin.createEnumerator, "POST", formData);
      setFormData(EMPTY_FORM); setShowCreate(false); fetchData();
    } catch (err) { setError(err.message || "Failed to create enumerator"); }
    finally { setSubmitting(false); }
  };

  const openEdit = (en) => {
    setToEdit(en);
    setFormData({ name: en.name, email: en.email, work_number: en.work_number || "", region_id: en.region_id || "", role: en.role || "enumerator" });
    setShowEdit(true);
  };

  const handleUpdate = async () => {
    if (!formData.name || !formData.email || !formData.region_id) { setError("Name, email and region are required"); return; }
    setSubmitting(true);
    try {
      await apiRequest(API_URLS.admin.editEnumerator(toEdit.id), "PUT", formData);
      setFormData(EMPTY_FORM); setShowEdit(false); setToEdit(null); fetchData();
    } catch (err) { setError(err.message || "Failed to update enumerator"); }
    finally { setSubmitting(false); }
  };

  const handleDelete = async () => {
    if (!toDelete) return;
    try {
      await apiRequest(API_URLS.admin.deleteEnumerator(toDelete), "DELETE");
      setShowDelete(false); setToDelete(null); fetchData();
    } catch (err) { setError(err.message || "Failed to delete enumerator"); }
  };

  const filtered = enumerators.filter(en => {
    const matchSearch = !search || en.name?.toLowerCase().includes(search.toLowerCase()) || en.email?.toLowerCase().includes(search.toLowerCase());
    const matchRegion = !filterRegion || String(en.region_id) === String(filterRegion);
    return matchSearch && matchRegion;
  });

  const toDeleteName = enumerators.find(e => e.id === toDelete)?.name;

  return (
    <div className="min-h-screen bg-slate-50">

      {/* ── Page header ───────────────────────────────────────────────────── */}
      <div className="bg-white border-b border-slate-200 px-6 py-5 sticky top-0 z-10 shadow-sm">
        <div className="max-w-screen-xl mx-auto flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <h1 className="text-xl font-bold text-slate-900 tracking-tight">Enumerators</h1>
            <p className="text-xs text-slate-400 mt-0.5">Manage field agents and their region assignments</p>
          </div>
          <button onClick={() => { setFormData(EMPTY_FORM); setShowCreate(true); }}
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold transition-colors">
            <Icon d={ICONS.plus} className="w-4 h-4" />
            Add Enumerator
          </button>
        </div>
      </div>

      <div className="max-w-screen-xl mx-auto px-4 sm:px-6 py-6 space-y-5">
        {error && <Alert type="error" message={error} onClose={() => setError("")} />}

        {/* ── Summary strip ──────────────────────────────────────────────── */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          {[
            { label: "Total Enumerators", value: enumerators.length,                                              color: "bg-blue-600",   icon: ICONS.users  },
            { label: "Regions Covered",   value: new Set(enumerators.map(e => e.region_id).filter(Boolean)).size, color: "bg-emerald-600", icon: ICONS.globe  },
            { label: "Unassigned",        value: enumerators.filter(e => !e.region_id).length,                    color: "bg-amber-500",   icon: ICONS.user   },
            { label: "Showing",           value: filtered.length,                                                  color: "bg-violet-600",  icon: ICONS.search },
          ].map((c, i) => (
            <div key={i} className="bg-white rounded-xl border border-slate-200 shadow-sm p-4 flex items-center gap-3">
              <div className={`w-9 h-9 ${c.color} rounded-lg flex items-center justify-center shrink-0`}>
                <Icon d={c.icon} className="w-4 h-4 text-white" />
              </div>
              <div>
                <p className="text-xl font-bold text-slate-900 leading-none">{c.value}</p>
                <p className="text-xs text-slate-500 mt-1 font-medium">{c.label}</p>
              </div>
            </div>
          ))}
        </div>

        {/* ── Table card ─────────────────────────────────────────────────── */}
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
          <div className="px-5 py-4 border-b border-slate-100 flex flex-col sm:flex-row sm:items-center gap-3">
            <div className="flex-1">
              <h2 className="text-base font-semibold text-slate-800">All Enumerators</h2>
              <p className="text-xs text-slate-400 mt-0.5">{filtered.length} of {enumerators.length} enumerators</p>
            </div>
            <div className="flex flex-wrap gap-2">
              <div className="relative">
                <Icon d={ICONS.search} className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
                <input type="text" value={search} onChange={e => setSearch(e.target.value)}
                  placeholder="Search name or email…"
                  className="pl-9 pr-3 py-2 text-sm border border-slate-200 rounded-lg bg-slate-50 focus:bg-white focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition w-52" />
                {search && (
                  <button onClick={() => setSearch("")} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
                    <Icon d={ICONS.x} className="w-3.5 h-3.5" />
                  </button>
                )}
              </div>
              <div className="relative">
                <select value={filterRegion} onChange={e => setFilterRegion(e.target.value)}
                  className="pl-3 pr-8 py-2 text-sm border border-slate-200 rounded-lg bg-slate-50 focus:bg-white focus:ring-2 focus:ring-blue-500 appearance-none outline-none transition">
                  <option value="">All Regions</option>
                  {regions.map(r => <option key={r.id} value={r.id}>{r.name}</option>)}
                </select>
                <div className="pointer-events-none absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400">
                  <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </div>
              </div>
            </div>
          </div>

          {loading ? (
            <div className="flex flex-col items-center justify-center py-16 gap-3">
              <div className="relative w-10 h-10">
                <div className="absolute inset-0 rounded-full border-4 border-blue-100" />
                <div className="absolute inset-0 rounded-full border-4 border-blue-600 border-t-transparent animate-spin" />
              </div>
              <p className="text-sm text-slate-400">Loading enumerators…</p>
            </div>
          ) : filtered.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 gap-3 text-slate-400">
              <div className="w-12 h-12 rounded-full bg-slate-100 flex items-center justify-center">
                <Icon d={ICONS.users} className="w-6 h-6 text-slate-300" />
              </div>
              <p className="text-sm font-medium">{search || filterRegion ? "No enumerators match your filters" : "No enumerators yet"}</p>
              {!search && !filterRegion && (
                <button onClick={() => { setFormData(EMPTY_FORM); setShowCreate(true); }} className="text-xs text-blue-600 hover:text-blue-800 font-semibold">
                  Add your first enumerator →
                </button>
              )}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead>
                  <tr className="bg-slate-50 border-b border-slate-100">
                    {["Enumerator","Email","Work Number","Region","Joined","Actions"].map(h => (
                      <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider whitespace-nowrap">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {filtered.map((en, i) => {
                    const initials   = en.name?.split(" ").map(w => w[0]).slice(0,2).join("").toUpperCase() || "?";
                    const dotColor   = PALETTE[i % PALETTE.length];
                    const regionName = en.region?.name || regions.find(r => String(r.id) === String(en.region_id))?.name;
                    return (
                      <tr key={en.id} className="hover:bg-slate-50/60 transition-colors">
                        <td className="px-4 py-3 whitespace-nowrap">
                          <div className="flex items-center gap-3">
                            <div className={`w-8 h-8 ${dotColor} rounded-full flex items-center justify-center text-xs font-bold text-white shrink-0`}>{initials}</div>
                            <span className="font-semibold text-slate-800">{en.name}</span>
                          </div>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          <div className="flex items-center gap-1.5 text-slate-500">
                            <Icon d={ICONS.mail} className="w-3.5 h-3.5 text-slate-300 shrink-0" />{en.email}
                          </div>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          {en.work_number
                            ? <div className="flex items-center gap-1.5 text-slate-500"><Icon d={ICONS.phone} className="w-3.5 h-3.5 text-slate-300 shrink-0" />{en.work_number}</div>
                            : <span className="text-slate-300 text-xs">—</span>}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          <span className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-semibold border ${regionName ? "bg-blue-50 text-blue-700 border-blue-200" : "bg-slate-100 text-slate-400 border-slate-200"}`}>
                            <Icon d={ICONS.globe} className="w-3 h-3" />{regionName || "Unassigned"}
                          </span>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          <div className="flex items-center gap-1.5 text-slate-400 text-xs">
                            <Icon d={ICONS.calendar} className="w-3.5 h-3.5 shrink-0" />
                            {new Date(en.created_at).toLocaleDateString("en-GB", { day:"2-digit", month:"short", year:"numeric" })}
                          </div>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          <div className="flex items-center gap-2">
                            <button onClick={() => openEdit(en)} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold bg-blue-50 text-blue-700 border border-blue-100 hover:bg-blue-100 transition-colors">
                              <Icon d={ICONS.edit} className="w-3.5 h-3.5" />Edit
                            </button>
                            <button onClick={() => { setToDelete(en.id); setShowDelete(true); }} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold bg-red-50 text-red-600 border border-red-100 hover:bg-red-100 transition-colors">
                              <Icon d={ICONS.trash} className="w-3.5 h-3.5" />Delete
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* ── Create Modal ──────────────────────────────────────────────────── */}
      {showCreate && (
        <Modal icon={ICONS.plus} iconBg="bg-blue-600" title="Add Enumerator" subtitle="Register a new field agent"
          onClose={() => { setShowCreate(false); setFormData(EMPTY_FORM); }}
          footer={
            <div className="flex gap-3">
              <SubmitBtn label="Create Enumerator" loadingLabel="Creating…" onClick={handleCreate} submitting={submitting} />
              <CancelBtn onClick={() => { setShowCreate(false); setFormData(EMPTY_FORM); }} />
            </div>
          }>
          <EnumeratorFormBody formData={formData} onChange={handleFieldChange} regions={regions} />
        </Modal>
      )}

      {/* ── Edit Modal ────────────────────────────────────────────────────── */}
      {showEdit && (
        <Modal icon={ICONS.edit} iconBg="bg-indigo-600" title="Edit Enumerator" subtitle={`Updating: ${toEdit?.name}`}
          onClose={() => { setShowEdit(false); setToEdit(null); setFormData(EMPTY_FORM); }}
          footer={
            <div className="flex gap-3">
              <SubmitBtn label="Save Changes" loadingLabel="Saving…" onClick={handleUpdate} submitting={submitting} />
              <CancelBtn onClick={() => { setShowEdit(false); setToEdit(null); setFormData(EMPTY_FORM); }} />
            </div>
          }>
          <EnumeratorFormBody formData={formData} onChange={handleFieldChange} regions={regions} />
        </Modal>
      )}

      {/* ── Delete Confirm Modal ──────────────────────────────────────────── */}
      {showDelete && (
        <div className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm">
            <div className="flex flex-col items-center text-center px-6 pt-8 pb-5">
              <div className="w-14 h-14 bg-red-50 rounded-2xl flex items-center justify-center mb-4 ring-8 ring-red-50">
                <Icon d={ICONS.warning} className="w-7 h-7 text-red-500" />
              </div>
              <h2 className="text-lg font-bold text-slate-900">Delete Enumerator</h2>
              <p className="text-sm text-slate-500 mt-2 leading-relaxed">
                Are you sure you want to delete{" "}
                <span className="font-semibold text-slate-800">"{toDeleteName}"</span>?
                This action cannot be undone.
              </p>
            </div>
            <div className="flex gap-3 px-6 pb-6">
              <button onClick={handleDelete} className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 bg-red-600 hover:bg-red-700 text-white text-sm font-semibold rounded-xl transition-colors">
                <Icon d={ICONS.trash} className="w-4 h-4" />Delete
              </button>
              <button onClick={() => { setShowDelete(false); setToDelete(null); }} className="flex-1 px-4 py-2.5 bg-slate-100 hover:bg-slate-200 text-slate-700 text-sm font-semibold rounded-xl transition-colors">
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}